/**
 * SkeletonCard — placeholder for card content.
 *
 * Renders a shimmer rectangle at the specified height.
 * Uses only CSS custom-property references.
 *
 * @see specs/integration-plan.md Section 5.1
 */
import React from "react";
import { useShimmer } from "./useShimmer";

const SKELETON_ANIMATION = "skel 1.2s ease-in-out infinite";

const SKELETON_GRADIENT =
  "linear-gradient(90deg, var(--bg2) 0%, rgba(0,0,0,0.06) 50%, var(--bg2) 100%)";

export interface SkeletonCardProps {
  /** Height of the card placeholder. Default: 200. */
  height?: string | number;
}

const shimmerBackground =
  SKELETON_GRADIENT;

export function SkeletonCard({
  height = 200,
}: SkeletonCardProps): React.ReactElement {
  const animate = useShimmer();
  const heightValue = typeof height === "number" ? `${height}px` : height;

  return (
    <div
      data-testid="skeleton-card"
      aria-hidden="true"
      style={{
        width: "100%",
        height: heightValue,
        borderRadius: "var(--radius-md, 8px)",
        background: shimmerBackground,
        backgroundSize: "200% 100%",
        animation: animate
          ? SKELETON_ANIMATION
          : "none",
      }}
    />
  );
}
